import React, { lazy, Suspense, useEffect } from 'react';
import { Navigate, useRoutes } from 'react-router-dom';
import { AlreadyLogin, CheckLogin } from '../auth/authContext';
import { RoutePermission } from '../auth/permissionContext';
import { ForgotPassword } from '../auth/views/forgotPassword';
import { ResetPassword } from '../auth/views/resetPassword';
import Login from '../auth/views/login';
import { RolesTypes } from '../config/roles';
import { AppRoutes } from '../constants/appRoutes';
import { BorrowerStatuses } from '../constants/borrowerStatus';
import { FollowUpStatus } from '../constants/followUpStatus';
import { InvestorStatuses } from '../constants/investorStatus';
import { LoanStatuses } from '../constants/loanStatus';
import { PageLoader } from '../shared/formUtilities/usePageLoader';
import { AutoInvestmentCreate } from '../views/autoInvestment/create';
import { ComparisonCreate } from '../views/cms/comparisons/comparisonCreate';
import { ComparisonList } from '../views/cms/comparisons/comparisonList';
import { PageNotFound } from '../views/pageNotFound/pageNotFound';
import { PromotionalCodeCreate } from '../views/promotionalCode/create';
import { PromotionalCodeList } from '../views/promotionalCode/list';
import { CorporateInvestorShareholder } from '../views/reports/corporateInvestorShareholders';
import DFSAReport from '../views/reports/dfsa';
import { MarketingReport } from '../views/reports/marketing';
import { LoanTapeReport } from '../views/reports/loanTape';
import PromoCodeReport from '../views/reports/promoCodeReport';
import { UserSurvey } from '../views/reports/userSurvey';
import { StaticPageCreate } from '../views/staticPages/createStaticPage';
import { UnauthorizedAccess } from '../views/unauthorizedAccess';
import DashboardLayout from './../shared/layout/dashboardLayout';
import DFSAInvestorOpportunities from '../views/reports/dfsaInvestorOpportunities';
import { SMSNotificationList } from '../views/smsNotifications/smsList';
import { SMSNotificationCreate } from '../views/smsNotifications/smsCreate';
import InvestorsWithdrawal from '../views/reports/investorWithdrawals';

const AdminUsers = lazy(() => import('../views/adminUsers'));
const Dashboard = lazy(() => import('../views/dashboard'));
const Investor = lazyWithPreload(() => import('../views/investors/investorList'));
const InvestorDeatil = lazyWithPreload(() => import('../views/investors/investorDetail'));
const Borrower = lazyWithPreload(() => import('../views/borrowers/borrowerList'));
const BorrowerDeatil = lazyWithPreload(() => import('../views/borrowers/borrowerDetail'));
const Loans = lazyWithPreload(() => import('../views/loans/loanList'));
const LoanCollections = lazyWithPreload(() => import('../views/loans/collections'));
const LoanDetail = lazyWithPreload(() => import('../views/loans/loanDetails'));
const Transactions = lazyWithPreload(() => import('../views/transactions'));
const Notifications = lazy(() => import('../views/notifications'));
const Members = lazy(() => import('../views/members/membersList'));
const MembersCreate = lazy(() => import('../views/members/membersCreate'));
const AnnouncementBarList = lazy(() => import('../views/announcementBar/list'));
const AnnouncementBarCreate = lazy(() =>
	import('../views/announcementBar/create')
);
const PushNotifications = lazy(() =>
	import('../views/pushNotifications/pushNotificationList')
);
const PushNotificationDetail = lazy(() =>
	import('../views/pushNotifications/pushNotificationDetail')
);
const StaticPages = lazy(() => import('../views/staticPages/staticPageList'));
const InvestorSignUp = lazy(() => import('../views/reports/investorSignUp'));
const BorrowerSignUp = lazy(() => import('../views/reports/borrowerSignUp'));
const ApprovedInvestors = lazy(() =>
	import('../views/reports/approvedInvestor')
);
const BorrowerShareholders = lazy(() =>
	import('../views/reports/borrowerShareholders')
);
const InvestorsDeposit = lazy(() =>
	import('../views/reports/investorDeposits')
);
const AECBReport = lazy(() => import('../views/reports/aecb'));
const BlogCategory = lazy(() =>
	import('../views/cms/blog/blogCategory/categoryList')
);

const CRSDataReport = lazy(() => import('../views/reports/crsData'));
const InvestorDailyBalances = lazy(() => import('../views/reports/investorDailyBalances'));
const BlogCategoryCreate = lazy(() =>
	import('../views/cms/blog/blogCategory/createCategory')
);
const BlogArticle = lazy(() => import('../views/cms/blog/blogs/blogList'));
const BlogCreate = lazy(() => import('../views/cms/blog/blogs/createBlog'));
const FAQCategory = lazy(() =>
	import('../views/faqs/faqCategory/categoryList')
);
const FAQCategoryCreate = lazy(() =>
	import('../views/faqs/faqCategory/createCategory')
);
const FAQList = lazy(() => import('../views/faqs/faqsQuestions/faqList'));
const FAQCreate = lazy(() => import('../views/faqs/faqsQuestions/createFAQ'));
const ControlPanel = lazy(() => import('../views/controlPanel'));
const Buckets = lazy(() => import('../views/buckets/bucketList'));
const BucketDetail = lazy(() => import('../views/buckets/bucketDetail'));
const Subscribers = lazy(() => import('../views/subscribers/index'));

function lazyWithPreload(factory) {
	const Component = React.lazy(factory);
	Component.preload = factory; // Attach the factory as preload method
	return Component;
}

export default function Router() {

	useEffect(() => {
		Investor.preload();
		InvestorDeatil.preload();
		Borrower.preload();
		BorrowerDeatil.preload();
		Loans.preload();
		LoanDetail.preload();
		Transactions.preload();
	}, []);


	return useRoutes([
				{
					path: '',
					element: (
						<CheckLogin>
							<DashboardLayout />
						</CheckLogin>
					),
					children: [
						{ path: '', element: <Navigate to={AppRoutes.Dashboard} /> },
						{
							path: AppRoutes.Dashboard,
							element: (
								<Suspense fallback={<PageLoader></PageLoader>}>
									<Dashboard />
								</Suspense>
							),
						},
						{
							path: `${AppRoutes.Investor}`,
							children: [
								{
									path: ``,
									element: (
										<Navigate to={`${AppRoutes.Iapproved}/${AppRoutes.Todo}`} />
									),
								},
								{
									path: AppRoutes.IAll,
									element: (
										<RoutePermission authKey={RolesTypes.Investors}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Investor
													props={{
														data: {
															route: `${AppRoutes.IAll}`,
															status: '',
														},
													}}
												/>
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: AppRoutes.Incomplete,
									element: (
										<RoutePermission authKey={RolesTypes.Investors}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Investor
													props={{
														data: {
															route: `${AppRoutes.Incomplete}`,
															status: InvestorStatuses.InComplete,
														},
													}}
												/>
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: `${AppRoutes.Onboarding}/${AppRoutes.Todo}`,
									element: (
										<RoutePermission authKey={RolesTypes.Investors}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Investor
													props={{
														data: {
															route: `${AppRoutes.Onboarding}`,
															status: InvestorStatuses.Pending,
															extraFilter: `investorFollowUpStatus=${FollowUpStatus.Todo}`,
															name: 'To-Do',
														},
													}}
												/>
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: `${AppRoutes.Incomplete}/${AppRoutes.Todo}`,
									element: (
										<RoutePermission authKey={RolesTypes.Investors}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Investor
													props={{
														data: {
															route: `${AppRoutes.Incomplete}`,
															status: InvestorStatuses.InComplete,
															extraFilter: `investorFollowUpStatus=${FollowUpStatus.Todo}`,
															name: 'To-Do',
														},
													}}
												/>
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: `${AppRoutes.Incomplete}/${AppRoutes.Updated}`,
									element: (
										<RoutePermission authKey={RolesTypes.Investors}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Investor
													props={{
														data: {
															route: `${AppRoutes.Incomplete}`,
															status: InvestorStatuses.InComplete,
															extraFilter: `investorFollowUpStatus=${FollowUpStatus.Done}`,
															name: 'Updated',
														},
													}}
												/>
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: `${AppRoutes.Incomplete}/${AppRoutes.FollowUp}`,
									element: (
										<RoutePermission authKey={RolesTypes.Investors}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Investor
													props={{
														data: {
															route: `${AppRoutes.Incomplete}`,
															status: InvestorStatuses.InComplete,
															extraFilter: `investorFollowUpStatus=${FollowUpStatus.FollowUp}`,
															name: 'Follow-up',
														},
													}}
												/>
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: AppRoutes.Onboarding,
									element: (
										<RoutePermission authKey={RolesTypes.Investors}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Investor
													props={{
														data: {
															route: `${AppRoutes.Onboarding}`,
															status: InvestorStatuses.Pending,
														},
													}}
												/>
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: `${AppRoutes.Onboarding}/${AppRoutes.Todo}`,
									element: (
										<RoutePermission authKey={RolesTypes.Investors}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Investor
													props={{
														data: {
															route: `${AppRoutes.Onboarding}`,
															status: InvestorStatuses.Pending,
															extraFilter: `investorFollowUpStatus=${FollowUpStatus.Todo}`,
															name: 'To-Do',
														},
													}}
												/>
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: `${AppRoutes.Onboarding}/${AppRoutes.Updated}`,
									element: (
										<RoutePermission authKey={RolesTypes.Investors}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Investor
													props={{
														data: {
															route: `${AppRoutes.Onboarding}`,
															status: InvestorStatuses.Pending,
															extraFilter: `investorFollowUpStatus=${FollowUpStatus.Done}`,
															name: 'Updated',
														},
													}}
												/>
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: `${AppRoutes.Onboarding}/${AppRoutes.FollowUp}`,
									element: (
										<RoutePermission authKey={RolesTypes.Investors}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Investor
													props={{
														data: {
															route: `${AppRoutes.Onboarding}`,
															status: InvestorStatuses.Pending,
															extraFilter: `investorFollowUpStatus=${FollowUpStatus.FollowUp}`,
															name: 'Follow-up',
														},
													}}
												/>
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: AppRoutes.Icompliance,
									element: (
										<RoutePermission authKey={RolesTypes.Investors}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Investor
													props={{
														data: {
															route: `${AppRoutes.Icompliance}`,
															status: InvestorStatuses.ApprovedByAM,
														},
													}}
												/>
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: AppRoutes.Iapproved,
									element: (
										<RoutePermission authKey={RolesTypes.Investors}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Investor
													props={{
														data: {
															route: `${AppRoutes.Iapproved}`,
															status: InvestorStatuses.ApprovedByCO,
														},
													}}
												/>
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: `${AppRoutes.Iapproved}/${AppRoutes.Todo}`,
									element: (
										<RoutePermission authKey={RolesTypes.Investors}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Investor
													props={{
														data: {
															route: `${AppRoutes.Iapproved}`,
															status: InvestorStatuses.ApprovedByCO,
															extraFilter: `investorFollowUpStatus=${FollowUpStatus.Todo}`,
															name: 'To-Do',
														},
													}}
												/>
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: `${AppRoutes.Iapproved}/${AppRoutes.Updated}`,
									element: (
										<RoutePermission authKey={RolesTypes.Investors}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Investor
													props={{
														data: {
															route: `${AppRoutes.Iapproved}`,
															status: InvestorStatuses.ApprovedByCO,
															extraFilter: `investorFollowUpStatus=${FollowUpStatus.Done}`,
															name: 'Updated',
														},
													}}
												/>
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: `${AppRoutes.Iapproved}/${AppRoutes.FollowUp}`,
									element: (
										<RoutePermission authKey={RolesTypes.Investors}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Investor
													props={{
														data: {
															route: `${AppRoutes.Iapproved}`,
															status: InvestorStatuses.ApprovedByCO,
															extraFilter: `investorFollowUpStatus=${FollowUpStatus.FollowUp}`,
															name: 'Follow-up',
														},
													}}
												/>
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: AppRoutes.Iwinddown,
									element: (
										<Navigate to={AppRoutes.Todo} />
									),
								},
								{
									path: `${AppRoutes.Iwinddown}/${AppRoutes.Todo}`,
									element: (
										<RoutePermission authKey={RolesTypes.Investors}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Investor
													props={{
														data: {
															route: `${AppRoutes.Iwinddown}`,
															status: InvestorStatuses.WindDown,
															extraFilter: `investorFollowUpStatus=${FollowUpStatus.Todo}`,
															name: 'To-Do',
														},
													}}
												/>
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: `${AppRoutes.Iwinddown}/${AppRoutes.Updated}`,
									element: (
										<RoutePermission authKey={RolesTypes.Investors}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Investor
													props={{
														data: {
															route: `${AppRoutes.Iwinddown}`,
															status: InvestorStatuses.WindDown,
															extraFilter: `investorFollowUpStatus=${FollowUpStatus.Done}`,
															name: 'Updated',
														},
													}}
												/>
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: `${AppRoutes.Iwinddown}/${AppRoutes.FollowUp}`,
									element: (
										<RoutePermission authKey={RolesTypes.Investors}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Investor
													props={{
														data: {
															route: `${AppRoutes.Iwinddown}`,
															status: InvestorStatuses.WindDown,
															extraFilter: `investorFollowUpStatus=${FollowUpStatus.FollowUp}`,
															name: 'Follow-up',
														},
													}}
												/>
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: AppRoutes.Iblocked,
									element: (
										<RoutePermission authKey={RolesTypes.Investors}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Investor
													props={{
														data: {
															route: `${AppRoutes.Iblocked}`,
															status: InvestorStatuses.Blocked,
														},
													}}
												/>
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: AppRoutes.Iarchived,
									element: (
										<RoutePermission authKey={RolesTypes.Investors}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Investor
													props={{
														data: {
															route: `${AppRoutes.Iarchived}`,
															status: InvestorStatuses.Archived,
														},
													}}
												/>
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: AppRoutes.Irejected,
									element: (
										<RoutePermission authKey={RolesTypes.Investors}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Investor
													props={{
														data: {
															route: `${AppRoutes.Irejected}`,
															status: InvestorStatuses.Rejected,
														},
													}}
												/>
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: ':id',
									element: (
										<RoutePermission authKey={RolesTypes.Investors}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<InvestorDeatil />
											</Suspense>
										</RoutePermission>
									),
								},
							],
						},
						{
							path: `${AppRoutes.Borrowers}`,
							children: [
								{
									path: ``,
									element: <Navigate to={`${AppRoutes.Bapproved}/${AppRoutes.Todo}`} />,
								},
								{
									path: AppRoutes.BAll,
									element: (
										<RoutePermission authKey={RolesTypes.Borrowers}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Borrower
													props={{
														data: {
															route: `${AppRoutes.BAll}`,
															status: '',
														},
													}}
												/>
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: AppRoutes.BIncomplete,
									element: (
										<RoutePermission authKey={RolesTypes.Borrowers}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Borrower
													props={{
														data: {
															route: `${AppRoutes.BIncomplete}`,
															status: BorrowerStatuses.InComplete,
														},
													}}
												/>
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: `${AppRoutes.BIncomplete}/${AppRoutes.Todo}`,
									element: (
										<RoutePermission authKey={RolesTypes.Borrowers}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Borrower
													props={{
														data: {
															route: `${AppRoutes.BIncomplete}`,
															status: BorrowerStatuses.InComplete,
															extraFilter: `borrowerFollowUpStatus=${FollowUpStatus.Todo}`,
															name: 'To-Do',
														},
													}}
												/>
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: `${AppRoutes.BIncomplete}/${AppRoutes.Updated}`,
									element: (
										<RoutePermission authKey={RolesTypes.Borrowers}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Borrower
													props={{
														data: {
															route: `${AppRoutes.BIncomplete}`,
															status: BorrowerStatuses.InComplete,
															extraFilter: `borrowerFollowUpStatus=${FollowUpStatus.Done}`,
															name: 'Updated',
														},
													}}
												/>
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: `${AppRoutes.BIncomplete}/${AppRoutes.FollowUp}`,
									element: (
										<RoutePermission authKey={RolesTypes.Borrowers}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Borrower
													props={{
														data: {
															route: `${AppRoutes.BIncomplete}`,
															status: BorrowerStatuses.InComplete,
															extraFilter: `borrowerFollowUpStatus=${FollowUpStatus.FollowUp}`,
															name: 'Follow-up',
														},
													}}
												/>
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: AppRoutes.BOnboarding,
									element: (
										<RoutePermission authKey={RolesTypes.Borrowers}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Borrower
													props={{
														data: {
															route: `${AppRoutes.BOnboarding}`,
															status: BorrowerStatuses.Pending,
														},
													}}
												/>
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: `${AppRoutes.BOnboarding}/${AppRoutes.Todo}`,
									element: (
										<RoutePermission authKey={RolesTypes.Borrowers}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Borrower
													props={{
														data: {
															route: `${AppRoutes.BOnboarding}`,
															status: BorrowerStatuses.Pending,
															extraFilter: `borrowerFollowUpStatus=${FollowUpStatus.Todo}`,
															name: 'To-Do',
														},
													}}
												/>
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: `${AppRoutes.BOnboarding}/${AppRoutes.Updated}`,
									element: (
										<RoutePermission authKey={RolesTypes.Borrowers}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Borrower
													props={{
														data: {
															route: `${AppRoutes.BOnboarding}`,
															status: BorrowerStatuses.Pending,
															extraFilter: `borrowerFollowUpStatus=${FollowUpStatus.Done}`,
															name: 'Updated',
														},
													}}
												/>
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: `${AppRoutes.BOnboarding}/${AppRoutes.FollowUp}`,
									element: (
										<RoutePermission authKey={RolesTypes.Borrowers}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Borrower
													props={{
														data: {
															route: `${AppRoutes.BOnboarding}`,
															status: BorrowerStatuses.Pending,
															extraFilter: `borrowerFollowUpStatus=${FollowUpStatus.FollowUp}`,
															name: 'Follow-up',
														},
													}}
												/>
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: AppRoutes.Bcompliance,
									element: (
										<RoutePermission authKey={RolesTypes.Borrowers}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Borrower
													props={{
														data: {
															route: `${AppRoutes.Bcompliance}`,
															status: BorrowerStatuses.ApprovedByAM,
														},
													}}
												/>
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: AppRoutes.Bapproved,
									element: (
										<RoutePermission authKey={RolesTypes.Borrowers}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Borrower
													props={{
														data: {
															route: `${AppRoutes.Bapproved}`,
															status: BorrowerStatuses.ApprovedByCO,
														},
													}}
												/>
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: `${AppRoutes.Bapproved}/${AppRoutes.Todo}`,
									element: (
										<RoutePermission authKey={RolesTypes.Borrowers}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Borrower
													props={{
														data: {
															route: `${AppRoutes.Bapproved}`,
															status: BorrowerStatuses.ApprovedByCO,
															extraFilter: `borrowerFollowUpStatus=${FollowUpStatus.Todo}`,
															name: 'To-Do',
														},
													}}
												/>
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: `${AppRoutes.Bapproved}/${AppRoutes.Updated}`,
									element: (
										<RoutePermission authKey={RolesTypes.Borrowers}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Borrower
													props={{
														data: {
															route: `${AppRoutes.Bapproved}`,
															status: BorrowerStatuses.ApprovedByCO,
															extraFilter: `borrowerFollowUpStatus=${FollowUpStatus.Done}`,
															name: 'Updated',
														},
													}}
												/>
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: `${AppRoutes.Bapproved}/${AppRoutes.FollowUp}`,
									element: (
										<RoutePermission authKey={RolesTypes.Borrowers}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Borrower
													props={{
														data: {
															route: `${AppRoutes.Bapproved}`,
															status: BorrowerStatuses.ApprovedByCO,
															extraFilter: `borrowerFollowUpStatus=${FollowUpStatus.FollowUp}`,
															name: 'Follow-up',
														},
													}}
												/>
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: AppRoutes.Barchived,
									element: (
										<RoutePermission authKey={RolesTypes.Borrowers}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Borrower
													props={{
														data: {
															route: `${AppRoutes.Barchived}`,
															status: BorrowerStatuses.Archived,
														},
													}}
												/>
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: ':id',
									element: (
										<RoutePermission authKey={RolesTypes.Borrowers}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<BorrowerDeatil />
											</Suspense>
										</RoutePermission>
									),
								},
							],
						},
						{
							path: `${AppRoutes.Loans}`,
							children: [
								{
									path: ``,
									element: <Navigate to={`${AppRoutes.Lpending}`} />,
								},
								{
									path: AppRoutes.LAll,
									element: (
										<RoutePermission authKey={RolesTypes.Loans}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Loans
													props={{
														data: {
															route: `${AppRoutes.LAll}`,
															status: "",
														},
													}}
												/>
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: AppRoutes.Lpending,
									element: (
										<RoutePermission authKey={RolesTypes.Loans}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Loans
													props={{
														data: {
															route: `${AppRoutes.Lpending}`,
															status: LoanStatuses.PreCredit,
														},
													}}
												/>
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: AppRoutes.Lcompliance,
									element: (
										<RoutePermission authKey={RolesTypes.Loans}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Loans
													props={{
														data: {
															route: `${AppRoutes.Lcompliance}`,
															status: LoanStatuses.Compliance,
														},
													}}
												/>
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: AppRoutes.Lprefundrasing,
									element: (
										<RoutePermission authKey={RolesTypes.Loans}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Loans
													props={{
														data: {
															route: `${AppRoutes.Lprefundrasing}`,
															status: LoanStatuses.PreFundraising,
														},
													}}
												/>
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: AppRoutes.Lcredit,
									element: (
										<RoutePermission authKey={RolesTypes.Loans}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Loans
													props={{
														data: {
															route: `${AppRoutes.Lcredit}`,
															status: LoanStatuses.Credit,
														},
													}}
												/>
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: AppRoutes.lpreCompliance,
									element: (
										<RoutePermission authKey={RolesTypes.Loans}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Loans
													props={{
														data: {
															route: `${AppRoutes.lpreCompliance}`,
															status: LoanStatuses.PreCompliance,
														},
													}}
												/>
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: AppRoutes.Lfundraising,
									element: (
										<RoutePermission authKey={RolesTypes.Loans}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Loans
													props={{
														data: {
															route: `${AppRoutes.Lfundraising}`,
															status: LoanStatuses.Fundraising,
														},
													}}
												/>
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: AppRoutes.LfullyFilled,
									element: (
										<RoutePermission authKey={RolesTypes.Loans}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Loans
													props={{
														data: {
															route: `${AppRoutes.LfullyFilled}`,
															status: LoanStatuses.FullyFilled,
														},
													}}
												/>
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: AppRoutes.Lcancelled,
									element: (
										<RoutePermission authKey={RolesTypes.Loans}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Loans
													props={{
														data: {
															route: `${AppRoutes.Lcancelled}`,
															status: LoanStatuses.Expired,
														},
													}}
												/>
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: AppRoutes.Lmonitor,
									element: (
										<RoutePermission authKey={RolesTypes.Loans}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Loans
													props={{
														data: {
															route: `${AppRoutes.Lmonitor}`,
															status: LoanStatuses.OnTime,
														},
													}}
												/>
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: AppRoutes.Llate,
									element: (
										<RoutePermission authKey={RolesTypes.Loans}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Loans
													props={{
														data: {
															route: `${AppRoutes.Llate}`,
															status: LoanStatuses.Late,
														},
													}}
												/>
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: AppRoutes.Lrestructured,
									element: (
										<RoutePermission authKey={RolesTypes.Loans}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Loans
													props={{
														data: {
															route: `${AppRoutes.Lrestructured}`,
															status: LoanStatuses.OnTime,
														},
													}}
												/>
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: AppRoutes.Lrejected,
									element: (
										<RoutePermission authKey={RolesTypes.Loans}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Loans
													props={{
														data: {
															route: `${AppRoutes.Lrejected}`,
															status: LoanStatuses.Rejected,
														},
													}}
												/>
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: AppRoutes.Ldefault,
									element: (
										<RoutePermission authKey={RolesTypes.Loans}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Loans
													props={{
														data: {
															route: `${AppRoutes.Ldefault}`,
															status: LoanStatuses.Default,
														},
													}}
												/>
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: AppRoutes.Lcollections,
									element: (
										<RoutePermission authKey={RolesTypes.Loans}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<LoanCollections />
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: AppRoutes.LfullyPaid,
									element: (
										<RoutePermission authKey={RolesTypes.Loans}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Loans
													props={{
														data: {
															route: `${AppRoutes.LfullyPaid}`,
															status: LoanStatuses.FullyPaid,
														},
													}}
												/>
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: ':id',
									element: (
										<RoutePermission authKey={RolesTypes.Loans}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<LoanDetail />
											</Suspense>
										</RoutePermission>
									),
								},
							],
						},
						{
							path: AppRoutes.Transactions,
							element: (
								<RoutePermission authKey={RolesTypes.Transactions}>
									<Suspense fallback={<PageLoader></PageLoader>}>
										<Transactions />
									</Suspense>
								</RoutePermission>
							),
						},
						{
							path: AppRoutes.Notification,
							element: (
								<Suspense fallback={<PageLoader></PageLoader>}>
									<Notifications />
								</Suspense>
							),
						},
						{
							path: AppRoutes.Members,
							children: [
								{
									path: '',
									element: (
										<RoutePermission authKey={RolesTypes.TeamMemberCRUD}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Members />
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: 'create',
									element: (
										<RoutePermission authKey={RolesTypes.TeamMemberCRUD}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<MembersCreate />
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: 'edit/:id',
									element: (
										<RoutePermission authKey={RolesTypes.TeamMemberCRUD}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<MembersCreate />
											</Suspense>
										</RoutePermission>
									),
								},
							],
						},
						{
							path: AppRoutes.PromotionalCode,
							children: [
								{
									path: '',
									element: (
										<RoutePermission authKey={RolesTypes.PromotionalCode}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<PromotionalCodeList />
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: 'create',
									element: (
										<RoutePermission authKey={RolesTypes.PromotionalCode}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<PromotionalCodeCreate />
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: 'edit/:id',
									element: (
										<RoutePermission authKey={RolesTypes.PromotionalCode}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<PromotionalCodeCreate />
											</Suspense>
										</RoutePermission>
									),
								},
							],
						},
						{
							path: AppRoutes.AnnouncementBar,
							children: [
								{
									path: '',
									element: (
										<RoutePermission authKey={RolesTypes.AnnouncementBar}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<AnnouncementBarList />
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: 'create',
									element: (
										<RoutePermission authKey={RolesTypes.AnnouncementBar}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<AnnouncementBarCreate />
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: 'edit/:id',
									element: (
										<RoutePermission authKey={RolesTypes.AnnouncementBar}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<AnnouncementBarCreate />
											</Suspense>
										</RoutePermission>
									),
								},
							],
						},
						{
							path: AppRoutes.PushNotification,
							children: [
								{
									path: '',
									element: (
										<RoutePermission authKey={RolesTypes.PushNotifications}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<PushNotifications />
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: 'create',
									element: (
										<RoutePermission authKey={RolesTypes.PushNotifications}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<PushNotificationDetail />
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: 'edit/:id',
									element: (
										<RoutePermission authKey={RolesTypes.PushNotifications}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<PushNotificationDetail />
											</Suspense>
										</RoutePermission>
									),
								},
							],
						},
						{
							path: AppRoutes.SMSNotification,
							children: [
								{
									path: '',
									element: (
										<RoutePermission authKey={RolesTypes.SMSNotifications}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<SMSNotificationList />
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: 'create',
									element: (
										<RoutePermission authKey={RolesTypes.SMSNotifications}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<SMSNotificationCreate />
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: 'edit/:id',
									element: (
										<RoutePermission authKey={RolesTypes.SMSNotifications}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<SMSNotificationCreate />
											</Suspense>
										</RoutePermission>
									),
								},
							],
						},
						{
							path: AppRoutes.StaticPages,
							children: [
								{
									path: '',
									element: (
										<RoutePermission authKey={RolesTypes.StaticPages}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<StaticPages />
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: 'create',
									element: (
										<RoutePermission authKey={RolesTypes.StaticPages}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<StaticPageCreate />
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: 'edit/:slug',
									element: (
										<RoutePermission authKey={RolesTypes.StaticPages}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<StaticPageCreate />
											</Suspense>
										</RoutePermission>
									),
								},
							],
						},
						{
							path: `${AppRoutes.Reports}`,
							children: [
								{
									path: '',
									element: (
										<Navigate
											to={`${AppRoutes.Reports}/${AppRoutes.ReportInvestorSignUp}`}
										/>
									),
								},
								{
									path: AppRoutes.ReportInvestorSignUp,
									element: (
										<RoutePermission authKey={RolesTypes.Reports}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<InvestorSignUp />
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: AppRoutes.ReportBorrowerSignUp,
									element: (
										<RoutePermission authKey={RolesTypes.Reports}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<BorrowerSignUp />
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: AppRoutes.ReportApprovedInvestor,
									element: (
										<RoutePermission authKey={RolesTypes.Reports}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<ApprovedInvestors />
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: AppRoutes.ReportPromoCode,
									element: (
										<RoutePermission authKey={RolesTypes.Reports}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<PromoCodeReport />
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: AppRoutes.ReportBorrowerShareholders,
									element: (
										<RoutePermission authKey={RolesTypes.Reports}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<BorrowerShareholders />
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: AppRoutes.ReportInvestorsDeposit,
									element: (
										<RoutePermission authKey={RolesTypes.Reports}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<InvestorsDeposit />
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: AppRoutes.ReportInvestorsWithdrawal,
									element: (
										<RoutePermission authKey={RolesTypes.Reports}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<InvestorsWithdrawal />
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: AppRoutes.ReportAECB,
									element: (
										<RoutePermission authKey={RolesTypes.Reports}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<AECBReport />
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: AppRoutes.ReportCorporateInvestorShareholder,
									element: (
										<RoutePermission authKey={RolesTypes.Reports}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<CorporateInvestorShareholder />
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: AppRoutes.ReportCRSData,
									element: (
										<RoutePermission authKey={RolesTypes.Reports}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<CRSDataReport />
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: AppRoutes.ReportLoanTape,
									element: (
										<RoutePermission authKey={RolesTypes.Reports}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<LoanTapeReport />
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: AppRoutes.ReportUserSurvey,
									element: (
										<RoutePermission authKey={RolesTypes.Reports}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<UserSurvey />
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: AppRoutes.ReportMarketing,
									element: (
										<RoutePermission authKey={RolesTypes.Reports}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<MarketingReport />
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: AppRoutes.ReportDFSA,
									element: (
										<RoutePermission authKey={RolesTypes.Reports}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<DFSAReport />
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: AppRoutes.ReportDFSAOpportunities,
									element: (
										<RoutePermission authKey={RolesTypes.Reports}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<DFSAInvestorOpportunities />
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: AppRoutes.ReportInvestorDailyBalances,
									element: (
										<RoutePermission authKey={RolesTypes.Reports}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<InvestorDailyBalances />
											</Suspense>
										</RoutePermission>
									),
								},
							],
						},
						{
							path: `${AppRoutes.Blog}`,
							children: [
								{
									path: '',
									element: (
										<Navigate
											to={`${AppRoutes.Blog}/${AppRoutes.BlogCategory}`}
										/>
									),
								},
								{
									path: AppRoutes.BlogCategory,
									children: [
										{
											path: '',
											element: (
												<RoutePermission authKey={RolesTypes.BlogCategoryCRUD}>
													<Suspense fallback={<PageLoader></PageLoader>}>
														<BlogCategory />
													</Suspense>
												</RoutePermission>
											),
										},
										{
											path: 'create',
											element: (
												<RoutePermission authKey={RolesTypes.BlogCategoryCRUD}>
													<Suspense fallback={<PageLoader></PageLoader>}>
														<BlogCategoryCreate />
													</Suspense>
												</RoutePermission>
											),
										},
										{
											path: 'edit/:id',
											element: (
												<RoutePermission authKey={RolesTypes.BlogCategoryCRUD}>
													<Suspense fallback={<PageLoader></PageLoader>}>
														<BlogCategoryCreate />
													</Suspense>
												</RoutePermission>
											),
										},
									],
								},
								{
									path: AppRoutes.BlogArticle,
									children: [
										{
											path: '',
											element: (
												<RoutePermission authKey={RolesTypes.BlogArticleCRUD}>
													<Suspense fallback={<PageLoader></PageLoader>}>
														<BlogArticle />
													</Suspense>
												</RoutePermission>
											),
										},
										{
											path: 'create',
											element: (
												<RoutePermission authKey={RolesTypes.BlogArticleCRUD}>
													<Suspense fallback={<PageLoader></PageLoader>}>
														<BlogCreate />
													</Suspense>
												</RoutePermission>
											),
										},
										{
											path: 'edit/:id',
											element: (
												<RoutePermission authKey={RolesTypes.BlogArticleCRUD}>
													<Suspense fallback={<PageLoader></PageLoader>}>
														<BlogCreate />
													</Suspense>
												</RoutePermission>
											),
										},
									],
								},
							],
						},
						{
							path: `${AppRoutes.FAQ}`,
							children: [
								{
									path: '',
									element: (
										<Navigate
											to={`${AppRoutes.FAQ}/${AppRoutes.FAQCategory}`}
										/>
									),
								},
								{
									path: AppRoutes.FAQCategory,
									children: [
										{
											path: '',
											element: (
												<RoutePermission authKey={RolesTypes.FAQCategoryCRUD}>
													<Suspense fallback={<PageLoader></PageLoader>}>
														<FAQCategory />
													</Suspense>
												</RoutePermission>
											),
										},
										{
											path: 'create',
											element: (
												<RoutePermission authKey={RolesTypes.FAQCategoryCRUD}>
													<Suspense fallback={<PageLoader></PageLoader>}>
														<FAQCategoryCreate />
													</Suspense>
												</RoutePermission>
											),
										},
										{
											path: 'edit/:id',
											element: (
												<RoutePermission authKey={RolesTypes.FAQCategoryCRUD}>
													<Suspense fallback={<PageLoader></PageLoader>}>
														<FAQCategoryCreate />
													</Suspense>
												</RoutePermission>
											),
										},
									],
								},
								{
									path: AppRoutes.FAQQuestions,
									children: [
										{
											path: '',
											element: (
												<RoutePermission authKey={RolesTypes.FAQItemsCRUD}>
													<Suspense fallback={<PageLoader></PageLoader>}>
														<FAQList />
													</Suspense>
												</RoutePermission>
											),
										},
										{
											path: ':slag',
											element: (
												<RoutePermission authKey={RolesTypes.FAQItemsCRUD}>
													<Suspense fallback={<PageLoader></PageLoader>}>
														<FAQList />
													</Suspense>
												</RoutePermission>
											),
										},
										{
											path: 'create',
											element: (
												<RoutePermission authKey={RolesTypes.FAQItemsCRUD}>
													<Suspense fallback={<PageLoader></PageLoader>}>
														<FAQCreate />
													</Suspense>
												</RoutePermission>
											),
										},
										{
											path: 'edit/:id',
											element: (
												<RoutePermission authKey={RolesTypes.FAQItemsCRUD}>
													<Suspense fallback={<PageLoader></PageLoader>}>
														<FAQCreate />
													</Suspense>
												</RoutePermission>
											),
										},
									],
								},
							],
						},
						{
							path: AppRoutes.ControlPanel,
							element: (
								<RoutePermission
									authMultiKeys={[
										RolesTypes.ControlPanelAmlList,
										RolesTypes.ControlPanelClient,
										RolesTypes.ControlPanelMetaTags,
										RolesTypes.ControlPanelSystemSettings,
									]}>
									<Suspense fallback={<PageLoader></PageLoader>}>
										<ControlPanel />
									</Suspense>
								</RoutePermission>
							),
						},
						{
							path: AppRoutes.Bucket,
							children: [
								{
									path: '',
									element: (
										<RoutePermission authKey={RolesTypes.Buckets}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<Buckets />
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: ':id',
									element: (
										<RoutePermission authKey={RolesTypes.Buckets}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<BucketDetail />
											</Suspense>
										</RoutePermission>
									),
								},
							],
						},
						{
							path: AppRoutes.AutoInvestment,
							children: [
								{
									path: '',
									element: (
										<RoutePermission authKey={RolesTypes.AutoInvestment}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<AutoInvestmentCreate />
											</Suspense>
										</RoutePermission>
									),
								},
								// {
								// 	path: ':id',
								// 	element: (
								// 		<RoutePermission authKey={RolesTypes.AutoInvestment}>
								// 			<Suspense fallback={<PageLoader></PageLoader>}>
								// 				<AutoInvestmentCreate />
								// 			</Suspense>
								// 		</RoutePermission>
								// 	),
								// },
							],
						},
						{
							path: AppRoutes.CompetitorsComparisons,
							children: [
								{
									path: '',
									element: (
										<RoutePermission authKey={RolesTypes.ComparisonCRUD}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<ComparisonList />
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: 'create',
									element: (
										<RoutePermission authKey={RolesTypes.ComparisonCRUD}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<ComparisonCreate />
											</Suspense>
										</RoutePermission>
									),
								},
								{
									path: 'edit/:id',
									element: (
										<RoutePermission authKey={RolesTypes.ComparisonCRUD}>
											<Suspense fallback={<PageLoader></PageLoader>}>
												<ComparisonCreate />
											</Suspense>
										</RoutePermission>
									),
								},
							],
						},
						{
							path: AppRoutes.Subscribers,
							element: (
								<RoutePermission authKey={RolesTypes.Subscribers}>
									<Suspense fallback={<PageLoader></PageLoader>}>
										<Subscribers />
									</Suspense>
								</RoutePermission>
							),
						},
						{
							path: AppRoutes.AdminUsers,
							element: (
								<RoutePermission authKey={RolesTypes.ControlPanelAdminUsers}>
									<Suspense fallback={<PageLoader></PageLoader>}>
										<AdminUsers />
									</Suspense>
								</RoutePermission>
							),
						},
					],
				},
				{
					path: `${AppRoutes.Login}`,
					element: (
						<AlreadyLogin>
							<Login></Login>
						</AlreadyLogin>
					),
				},
				{
					path: `${AppRoutes.ForgotPassword}`,
					element: <ForgotPassword></ForgotPassword>,
				},
				{
					path: `${AppRoutes.ResetPassword}`,
					element: <ResetPassword></ResetPassword>,
				},
				{
					path: AppRoutes.Unauthorized,
					element: <UnauthorizedAccess />,
				},
		{
			path: '*',
			element: <PageNotFound />,
		},
	]);
}
